import { useState } from "react";
import { supabase } from "./supabaseClient";
import { CredentiaWordmark } from "./components/CredentiaLogo";
import TurnstileWidget, { isTurnstileEnabled } from "./components/TurnstileWidget";
import { markSent, secondsUntilNextSend } from "./emailGuard";

const LAST_SEND_KEY = "credentia_app_last_magic_link";

export default function AuthScreen() {
  const [email, setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent]     = useState(false);
  const [error, setError]   = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaReset, setCaptchaReset] = useState(0);

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    const wait = secondsUntilNextSend(LAST_SEND_KEY);
    if (wait > 0) {
      setError(`A link was just sent. Please wait ${wait}s before requesting another.`);
      return;
    }
    if (isTurnstileEnabled && !captchaToken) {
      setError("Please complete the verification challenge.");
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}/app`,
        shouldCreateUser: false,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    setLoading(false);
    if (error) {
      setError(error.message);
      setCaptchaReset(n => n + 1);
      return;
    }
    markSent(LAST_SEND_KEY);
    setSent(true);
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#0F1F2E",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Inter','Segoe UI',sans-serif", padding: "20px",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=DM+Serif+Display&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .auth-input{width:100%;background:#162336;border:1px solid #1e3048;border-radius:9px;color:#F2F4F7;font-family:inherit;font-size:13.5px;padding:10px 14px;outline:none;transition:border .15s}
        .auth-input::placeholder{color:#3a5470}
        .auth-input:focus{border-color:#2FAF9F}
        .auth-btn{width:100%;padding:11px;border-radius:9px;border:none;background:#2FAF9F;color:#fff;font-family:inherit;font-size:13.5px;font-weight:600;cursor:pointer;transition:opacity .15s}
        .auth-btn:hover{opacity:.9}
        .auth-btn:disabled{opacity:.5;cursor:not-allowed}
      `}</style>

      <div style={{ width: "100%", maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
          <CredentiaWordmark iconSize={56} />
        </div>

        <div style={{ background: "#162336", border: "1px solid #1e3048", borderRadius: 16, padding: "28px 28px 24px" }}>
          {sent ? (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 14 }}>📬</div>
              <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 19, fontWeight: 700, color: "#F2F4F7", marginBottom: 8 }}>Check your email</h2>
              <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.65, marginBottom: 20 }}>
                We've sent a sign-in link to <strong style={{ color: "#7a9ab8" }}>{email}</strong>.<br />
                Click the link in the email to access your portfolio.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                style={{ background: "none", border: "1px solid #1e3048", borderRadius: 8, color: "#64748B", fontFamily: "inherit", fontSize: 12, padding: "7px 16px", cursor: "pointer" }}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 22 }}>
                <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 20, fontWeight: 700, color: "#F2F4F7" }}>Sign in</h2>
                <p style={{ fontSize: 12.5, color: "#64748B", marginTop: 4 }}>Enter your email and we'll send you a sign-in link — no password needed.</p>
                <p style={{ fontSize: 11.5, color: "#2a4a6a", marginTop: 10 }}>
                  The portfolio tracker is currently invite-only.{" "}
                  <a href="mailto:hello@credentia.pro" style={{ color: "#2FAF9F", textDecoration: "none" }}>Get in touch</a>{" "}
                  to request access.
                </p>
              </div>

              {error && (
                <div style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 8, padding: "9px 13px", color: "#fca5a5", fontSize: 12.5, marginBottom: 14 }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <input
                  className="auth-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                />
                <TurnstileWidget
                  onToken={setCaptchaToken}
                  onError={() => setError("Verification failed to load. Please refresh and try again.")}
                  resetSignal={captchaReset}
                />
                <button className="auth-btn" type="submit" disabled={loading || (isTurnstileEnabled && !captchaToken)} style={{ marginTop: 4 }}>
                  {loading ? "Sending…" : "Send sign-in link"}
                </button>
              </form>
            </>
          )}
        </div>

        <div style={{ marginTop: 20, fontSize: 10.5, color: "#2a4a6a", textAlign: "center" }}>
          A Medberry product · CESR portfolio management
        </div>
      </div>
    </div>
  );
}
